import { useTokenSelection } from '@/components/features/Account/AccountNFTList/useTokenSelection';
import { Image } from '@/components/shared/Image';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { useTokens } from '@/hooks/use-tokens';
import { useAuthentication } from '@/hooks/useAuthentication';
import { wagmiConfig } from '@/lib/wagmi';
import { Button, Flex, Heading, Text } from '@chakra-ui/react';
import { compareAddresses } from '@onbeam/utils';
import { WarningIcon } from '@sphere/icons';
import { QuantityLabel, scrollStyles } from '@sphere/ui';
import { simulateContract } from '@wagmi/core';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Address, erc721Abi } from 'viem';
import { TransferState } from './TransferBulk.types';

type Props = {
  unapprovedContracts: string[];
  setTransferState: Dispatch<SetStateAction<TransferState>>;
};

export const TransferBulkOverview = ({ unapprovedContracts, setTransferState }: Props) => {
  const { t } = useTranslation('account');
  const selectedTokens = useTokenSelection(state => state.selectedTokens);
  const { data: tokensData } = useTokens(
    selectedTokens.map(token => `${token.contractAddress}:${token.id}`),
    { limit: 100 },
  );

  const [untransferableContracts, setUntransferableContracts] = useState<string[]>([]);

  const marketplaceChain = useMarketplaceChain();
  const { userAddress } = useAuthentication();

  const sortedTokens = selectedTokens.map(selectedToken => ({
    ...selectedToken,
    ...tokensData?.tokens?.find(
      tokenData =>
        compareAddresses(tokenData.token?.contract, selectedToken.contractAddress) &&
        tokenData.token?.tokenId === selectedToken.id,
    ),
  }));

  /**
   * Simulates a call to the `setApprovalForAll` contract method to check if the contract is transferable.
   * Needed as some contracts do not allow tokens to be transfered without the tokenBeamer proxy to be
   * whitelisted first.
   */
  useEffect(() => {
    const getUntransferableContracts = async () => {
      const results = await Promise.all(
        unapprovedContracts.map(async contract => {
          const isTransferable = await simulateContract(wagmiConfig, {
            chainId: marketplaceChain.id,
            address: contract as Address,
            abi: erc721Abi,
            functionName: 'setApprovalForAll',
            args: [marketplaceChain.contracts?.tokenBeamer?.address!, true],
            account: userAddress!,
          })
            .then(() => true)
            .catch(() => false);

          return { contract, isTransferable };
        }),
      );

      return results
        .filter(({ isTransferable }) => !isTransferable)
        .map(({ contract }) => contract);
    };

    getUntransferableContracts().then(setUntransferableContracts);
  }, [
    unapprovedContracts,
    userAddress,
    marketplaceChain.contracts?.tokenBeamer?.address,
    marketplaceChain.id,
  ]);

  const handleContinue = () =>
    unapprovedContracts.length
      ? setTransferState(TransferState.APPROVE)
      : setTransferState(TransferState.TRANSFER);

  const canContinue = !untransferableContracts.length;

  return (
    <>
      <Heading fontSize="2xl" fontWeight="normal" mb="space.12">
        {t('assets.bulk-transfer.overview.title')}
      </Heading>
      <Flex direction="column" align="center" gap="space.8">
        <Text color="gray.6">{t('assets.bulk-transfer.overview.description')}</Text>
        <Text color="gray.6" fontSize="sm">
          {t('assets.bulk-transfer.overview.quantity', { count: selectedTokens.length })}
        </Text>
        <Flex
          w="full"
          direction="column"
          gap="space.12"
          maxH={rem(200)}
          overflowY="auto"
          css={scrollStyles}
          bg="gray.2"
          p="space.12"
          rounded="radius.8"
        >
          {sortedTokens.map(token => (
            <Flex
              key={`${token.contractAddress}:${token.id}`}
              justify="space-between"
              align="center"
            >
              <Flex gap="space.8" align="center">
                <Image
                  src={token.token?.image}
                  alt={`${token.token?.name} image`}
                  width={48}
                  height={48}
                  boxSize={rem(48)}
                  objectFit="contain"
                  rounded="radius.4"
                />
                {token.token?.name}
              </Flex>
              <Flex gap="space.12" align="center">
                {token.quantity > 1 && <QuantityLabel quantity={token.quantity} />}
                {untransferableContracts.some(contract =>
                  compareAddresses(contract, token.contractAddress),
                ) && <WarningIcon height={4} width={4} color="red" />}
              </Flex>
            </Flex>
          ))}
        </Flex>

        {!canContinue && (
          <Text color="red" fontSize="sm" mt="space.16" px="space.12" textAlign="center">
            <Text as="span">{t('assets.bulk-transfer.overview.not-transferable-error')}</Text>
          </Text>
        )}

        <Button onClick={handleContinue} w="full" mt="space.16" isDisabled={!canContinue}>
          {t('assets.bulk-transfer.overview.cta.continue')}
        </Button>
      </Flex>
    </>
  );
};
